'use client';

import { useCallback } from 'react';
import {
  useImperativeDrawerOrModal,
  type ImperativeDrawerOrModalOptions,
} from './imperative-drawer-or-modal-context';
import {
  ConfirmationDrawerOrModalAutoClose,
  type ConfirmationDrawerOrModalAutoCloseProps,
} from './confirmation-drawer-or-modal-auto-close';

type ConfirmationDrawerOrModalOptions = Omit<
  ConfirmationDrawerOrModalAutoCloseProps,
  'closeModal'
> &
  Pick<ImperativeDrawerOrModalOptions, 'isAboveBreakpoint' | 'drawerProps' | 'modalProps'>;

/**
 * Hook for responsive confirmation modals — renders as a modal on desktop and a drawer on mobile.
 *
 * @example
 * ```tsx
 * const { openModal, closeModal } = useConfirmationDrawerOrModal();
 *
 * openModal({
 *   title: 'Delete Item',
 *   text: 'Are you sure?',
 *   primaryButtonText: 'Delete',
 *   onPrimaryButtonClick: async () => { await deleteItem(); },
 *   secondaryButtonText: 'Cancel',
 *   variant: 'destructive',
 * });
 * ```
 */
export const useConfirmationDrawerOrModal = () => {
  const { openDrawerOrModal, closeDrawerOrModal } = useImperativeDrawerOrModal();

  const openModal = useCallback(
    (options: ConfirmationDrawerOrModalOptions) => {
      const { isAboveBreakpoint, showCloseButton, drawerProps, modalProps, ...confirmationProps } =
        options;
      openDrawerOrModal(
        <ConfirmationDrawerOrModalAutoClose
          {...confirmationProps}
          showCloseButton={showCloseButton}
          closeModal={closeDrawerOrModal}
        />,
        { isAboveBreakpoint, showCloseButton, drawerProps, modalProps },
      );
    },
    [openDrawerOrModal, closeDrawerOrModal],
  );

  return { openModal, closeModal: closeDrawerOrModal };
};
